import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import cx from 'classnames';
import { Tooltip } from 'shared/components/atoms/Tooltip';
import { RadioButtonIcon } from '../RadioButtonIcon';

interface IRadioButton {
  description: string | React.ReactNode;
  className?: string;
  id?: string;
  testId?: string;
  key?: string;
  inline?: boolean;
  size?: 'small' | 'medium' | 'large' | 'fill';
  selected?: boolean;
  status?: 'notSelected' | 'selected';
  disabled?: boolean;
  onClick?(event: any): void;
  tooltipText?: string | undefined;
  tooltipIcon?: string | undefined;
}

const RadioButton: FunctionComponent<React.PropsWithChildren<IRadioButton>> = ({
  onClick,
  description,
  className,
  id,
  testId,
  inline,
  size = 'fill',
  selected,
  status,
  disabled,
  tooltipText,
  tooltipIcon
}) => {
  return (
    <div
      data-testid={testId}
      className={cx(className, status, size, {
        inline,
        selected: !status && selected,
        notSelected: !status && !selected,
        disabled,
      })}
      id={`wrapper-${id}`}
      onClick={(e) => {
        e.preventDefault();
        if (!disabled && !!onClick) {
          onClick(e);
        }
      }}
    >
      <div className="radioButton__inner">
        <div className="radioButton__content">
          <label>
            <input type="radio" className="radioButton__input" id={id} disabled={disabled} />
            <div className="radioButton__icon-and-description">
              <RadioButtonIcon checked={status === 'selected' || selected} disabled={disabled} />
              <p className="radioButton__description font-weight-bold">
                {description}
                {!!tooltipText &&
                  <Tooltip
                    text={tooltipText}
                    style={tooltipIcon ? { marginLeft: '0.75rem' } : undefined}
                    iconProps={
                      tooltipIcon
                        ? { name: tooltipIcon, className: 'primary', round: true }
                        : undefined
                    } />
                }
              </p>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
};

export const RadioButtonDisabledOverride = styled.div`
  .disabled.selected {
    cursor: not-allowed;

    img {
      opacity: 0.5;
    }

    p,
    i {
      color: ${(props) => props.theme.colours.grey30};
    }
  }
`;

const StyledRadioButton = styled(RadioButton)`
  background-color: white;
  color: ${(props) => props.theme.colours.grey70};
  line-height: initial;
  user-select: none;
  border-radius: 4px;
  height: 52px;
  vertical-align: middle;
  width:100%;
  display: block;

  &.inline {
    display: inline-block;
  }
  
  &.small {
    width: 130px;
    font-size: 100%;
  }
  
  &.medium {
    width: 210px;
  }

  &.large {
    width: 250px;
  }

  &.fill {
    width: 100%;
  }  
  
  &.disabled {  
    img {
      opacity: 0.5;
    }
    
    p {
      color: ${(props) => props.theme.colours.grey30};
    }

    i {
      color: ${(props) => props.theme.colours.primaryB1};
    }

    &.selected {
      img {
        opacity: 1;
      }
    
      p, i {
        color: ${(props) => props.theme.colours.grey70};
      }
    }
  }
  
  &:hover {
    cursor: pointer;
  }

  &.disabled:hover {
    cursor: not-allowed;
  }

  [type='radio'] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  button:focus {
    outline: 0;
  }

  .radioButton__content {
    display: table;
    height: 100%;
    width: 100%;
    label {
      margin-bottom: 0;
      display: table-cell;
      vertical-align: middle;
    }
  }

  .radioButton__content {
    padding: 0 15px;
  }

  .radioButton__content label:hover {
    cursor: pointer;
  }

  &.disabled .radioButton__content label:hover {
    cursor: not-allowed;
  }
  
  .radioButton__inner {
    height:100%;
    width:100%;
    border 1.5px solid transparent;
  }

  &.notSelected {
    box-shadow: 0 2px 4px 0 rgba(0, 70, 135, 0.1);
    border: solid 0.5px ${(props) => props.theme.colours.secondaryC5};
  }

  &.selected {
    color: ${(props) => props.theme.colours.primaryB1};
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    border: solid 0.5px ${(props) => props.theme.colours.primaryB1};
    .radioButton__inner {
      border 1.5px solid ${(props) => props.theme.colours.primaryB1};
    }
  }

  .radioButton__icon-and-description {
    display: flex;

    .radioButton__description {
      margin-left: 0.4em;
      margin-top: auto;
      margin-bottom: auto;
      text-align: left;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-grow: 1;

      > span {
        margin-left: 0.3em;
      }
    }
  }
  
  &.big {
    .radioButton__description {
      padding-right: 50px;
    }
  }
  
  .icon {
    width: 29px;
    height: 29px;
  }

  .radioButton__icon-and-description:hover {
    cursor: pointer;
  }

  &.disabled .radioButton__icon-and-description:hover {
    cursor: not-allowed;
  }
`;

export default StyledRadioButton;
